import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useRef,
} from 'react'
import GlobalLoader from '../components/ui/GlobalLoader/GlobalLoader'

export interface ProgressState {
  current: number
  total: number
  label?: string
}

export interface LoaderInstance {
  id: string
  text?: string
  progress?: ProgressState
  tag?: string
}

interface LoaderContextType {
  showLoader: (text?: string, id?: string, tag?: string) => string
  hideLoader: (id?: string) => void
  setLoaderText: (text: string, id?: string) => void
  setLoaderTag: (tag: string, id?: string) => void
  setProgress: (
    current: number,
    total: number,
    label?: string,
    id?: string
  ) => void
  clearProgress: (id?: string) => void
}

const LoaderContext = createContext<LoaderContextType | undefined>(undefined)

export const LoaderProvider = ({ children }: { children: ReactNode }) => {
  const [loaders, setLoaders] = useState<LoaderInstance[]>([])
  const defaultIdRef = useRef<string | null>(null)

  const generateId = () =>
    `loader-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

  const showLoader = useCallback(
    (text?: string, id?: string, tag?: string): string => {
      const loaderId = id || generateId()

      if (!id) {
        defaultIdRef.current = loaderId
      }

      setLoaders((prev) => {
        const exists = prev.find((l) => l.id === loaderId)
        if (exists) {
          return prev.map((l) =>
            l.id === loaderId ? { ...l, text, tag, progress: undefined } : l
          )
        }
        return [...prev, { id: loaderId, text, tag, progress: undefined }]
      })

      return loaderId
    },
    []
  )

  const hideLoader = useCallback((id?: string) => {
    const targetId = id || defaultIdRef.current
    if (!targetId) return

    setLoaders((prev) => prev.filter((l) => l.id !== targetId))

    if (targetId === defaultIdRef.current) {
      defaultIdRef.current = null
    }
  }, [])

  const setLoaderText = useCallback((text: string, id?: string) => {
    const targetId = id || defaultIdRef.current
    if (!targetId) return

    setLoaders((prev) =>
      prev.map((l) => (l.id === targetId ? { ...l, text } : l))
    )
  }, [])

  const setLoaderTag = useCallback((tag: string, id?: string) => {
    const targetId = id || defaultIdRef.current
    if (!targetId) return

    setLoaders((prev) =>
      prev.map((l) => (l.id === targetId ? { ...l, tag } : l))
    )
  }, [])

  const setProgress = useCallback(
    (current: number, total: number, label?: string, id?: string) => {
      const targetId = id || defaultIdRef.current
      if (!targetId) return

      setLoaders((prev) =>
        prev.map((l) =>
          l.id === targetId ? { ...l, progress: { current, total, label } } : l
        )
      )
    },
    []
  )

  const clearProgress = useCallback((id?: string) => {
    const targetId = id || defaultIdRef.current
    if (!targetId) return

    setLoaders((prev) =>
      prev.map((l) => (l.id === targetId ? { ...l, progress: undefined } : l))
    )
  }, [])

  return (
    <LoaderContext.Provider
      value={{
        showLoader,
        hideLoader,
        setLoaderText,
        setLoaderTag,
        setProgress,
        clearProgress,
      }}
    >
      <GlobalLoader loaders={loaders} />
      {children}
    </LoaderContext.Provider>
  )
}

export const useLoader = (): LoaderContextType => {
  const context = useContext(LoaderContext)
  if (!context) {
    throw new Error('useLoader must be used within a LoaderProvider')
  }
  return context
}
